import type {
  ActionBody,
  AutomationBody,
  AutomationInputBody,
  TypeBody,
} from '@/shared/api/generated/model';

/*
 * Автоматизации (шаг 7.4) — чистые правила раздела: «когда» словами,
 * адресаты письма, заготовки действий. Отдельно от компонентов — их
 * проверяют тесты.
 */

export type ActionKind = ActionBody['kind'];

export const ACTION_TITLES: Record<ActionKind, string> = {
  email: 'Письмо',
  telegram: 'Telegram',
  webhook: 'Вебхук',
};

/** «Когда» словами: «Заявки: создана с сайта», «Заказы: статус → Оплачен». */
export function triggerText(automation: AutomationBody, types: TypeBody[]): string {
  const type = types.find((t) => t.id === automation.trigger.type_id);
  const name = type?.name ?? 'коллекция удалена';

  if (automation.trigger.event === 'record.stage') {
    const stage = (type?.workflow.statuses ?? []).find((s) => s.key === automation.trigger.stage);

    return `${name}: статус → ${stage?.name ?? 'любой'}${automation.trigger.from_site === true ? ' (с сайта)' : ''}`;
  }

  return `${name}: новая запись${automation.trigger.from_site === true ? ' с сайта' : ''}`;
}

/** Адресаты письма из строки: «team@x.ru, владелец» → ['team@x.ru', 'owner']. */
export function parseRecipients(text: string): string[] {
  return text
    .split(/[,;\n]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

/** Заготовка действия нужного вида. */
export function blankAction(kind: ActionKind): ActionBody {
  switch (kind) {
    case 'email':
      return {
        kind,
        to: [],
        subject: '{{collection}}: {{title}}',
        body: 'Новая запись на сайте {{site}}.\n\n{{fields}}\n\nОткрыть в админке: {{link}}',
      };
    case 'telegram':
      return { kind, chat_id: '', body: '{{collection}}: {{title}}\n\n{{fields}}\n\n{{link}}' };
    default:
      return { kind, url: '' };
  }
}

/** Тело запроса из того, что на экране: секреты — только если ввели новые. */
export function inputOf(
  name: string,
  enabled: boolean,
  trigger: AutomationInputBody['trigger'],
  actions: ActionBody[],
): AutomationInputBody {
  return {
    name: name.trim(),
    enabled,
    trigger,
    actions: actions.map((a) => {
      const out: ActionBody = { kind: a.kind };

      if (a.kind === 'email') {
        Object.assign(out, { to: a.to ?? [], subject: a.subject ?? '', body: a.body ?? '' });
      }

      if (a.kind === 'telegram') {
        Object.assign(out, {
          chat_id: a.chat_id ?? '',
          body: a.body ?? '',
          ...(a.bot_token ? { bot_token: a.bot_token } : {}),
        });
      }

      if (a.kind === 'webhook') {
        Object.assign(out, { url: a.url ?? '', ...(a.secret ? { secret: a.secret } : {}) });
      }

      return out;
    }),
  };
}
